/**
 * rb-photo.js — photo upload + crop modal.
 *
 * Self-contained: builds its own modal on first use. The crop frame is a fixed
 * 3:4 portrait (the standard résumé-photo ratio); the user pans by dragging and
 * zooms with a slider. Apply produces a downscaled JPEG data URL.
 *
 * Depends on: RbI18n. Optionally window.__toast.
 * globalThis.RbPhoto
 */
(function () {
    'use strict';

    var T = function (k) { return globalThis.RbI18n.t(k); };
    var MAX_BYTES = 8 * 1024 * 1024;
    var VIEW_W = 270, VIEW_H = 360;          /* crop viewport (3:4) */
    var OUT_W = 600, OUT_H = 800;            /* exported resolution */

    var _modal = null, _canvas = null, _ctx = null;
    var _img = null, _scale = 1, _minScale = 1, _ox = 0, _oy = 0, _cb = null;
    var _drag = null;

    function toast(msg, type) {
        if (window.__toast) window.__toast(msg, type || 'error');
    }

    function build() {
        if (_modal) return;
        _modal = document.createElement('div');
        _modal.className = 'rb-photo-modal';
        _modal.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;z-index:1200;display:none;align-items:center;' +
            'justify-content:center;background:rgba(0,0,0,.6);backdrop-filter:blur(4px)';
        _modal.innerHTML =
            '<div style="background:var(--bg-secondary,#1a1d29);border:1px solid rgba(255,255,255,.12);' +
            'border-radius:14px;padding:18px;max-width:340px;width:92%">' +
            '<h3 style="margin:0 0 12px;font:600 15px/1.3 inherit;color:var(--text-primary,#fff)">' + esc(T('crop_photo')) + '</h3>' +
            '<div class="rb-photo-stage" style="position:relative;width:' + VIEW_W + 'px;height:' + VIEW_H +
            'px;margin:0 auto;border-radius:8px;overflow:hidden;background:#111;cursor:grab;touch-action:none">' +
            '<canvas width="' + VIEW_W + '" height="' + VIEW_H + '" style="display:block"></canvas></div>' +
            '<input type="range" class="rb-photo-zoom" min="1" max="3" step="0.01" value="1" ' +
            'style="width:100%;margin:12px 0;accent-color:var(--accent,#2563eb)">' +
            '<div style="display:flex;gap:8px;justify-content:flex-end">' +
            '<button type="button" class="rb-btn rb-photo-cancel">' + esc(T('cancel') || 'Cancel') + '</button>' +
            '<button type="button" class="rb-btn rb-primary rb-photo-apply">' + esc(T('crop_photo')) + '</button>' +
            '</div></div>';
        document.body.appendChild(_modal);
        _canvas = _modal.querySelector('canvas');
        _ctx = _canvas.getContext('2d');

        var stage = _modal.querySelector('.rb-photo-stage');
        var zoom = _modal.querySelector('.rb-photo-zoom');
        zoom.addEventListener('input', function () {
            _scale = _minScale * parseFloat(zoom.value);
            clampOffsets(); paint();
        });
        function down(e) {
            _drag = { x: pointX(e), y: pointY(e), ox: _ox, oy: _oy };
            stage.style.cursor = 'grabbing';
        }
        function move(e) {
            if (!_drag) return;
            e.preventDefault();
            _ox = _drag.ox + (pointX(e) - _drag.x);
            _oy = _drag.oy + (pointY(e) - _drag.y);
            clampOffsets(); paint();
        }
        function up() { _drag = null; stage.style.cursor = 'grab'; }
        stage.addEventListener('mousedown', down);
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
        stage.addEventListener('touchstart', down, { passive: true });
        stage.addEventListener('touchmove', move, { passive: false });
        stage.addEventListener('touchend', up);

        _modal.querySelector('.rb-photo-cancel').addEventListener('click', close);
        _modal.querySelector('.rb-photo-apply').addEventListener('click', apply);
        _modal.addEventListener('click', function (e) { if (e.target === _modal) close(); });
    }

    function esc(s) { var d = document.createElement('div'); d.textContent = s == null ? '' : s; return d.innerHTML; }
    function pointX(e) { return e.touches ? e.touches[0].clientX : e.clientX; }
    function pointY(e) { return e.touches ? e.touches[0].clientY : e.clientY; }

    function clampOffsets() {
        var w = _img.width * _scale, h = _img.height * _scale;
        var minX = VIEW_W - w, minY = VIEW_H - h;
        _ox = Math.min(0, Math.max(minX, _ox));
        _oy = Math.min(0, Math.max(minY, _oy));
        if (w < VIEW_W) _ox = (VIEW_W - w) / 2;
        if (h < VIEW_H) _oy = (VIEW_H - h) / 2;
    }

    function paint() {
        _ctx.fillStyle = '#111';
        _ctx.fillRect(0, 0, VIEW_W, VIEW_H);
        _ctx.drawImage(_img, _ox, _oy, _img.width * _scale, _img.height * _scale);
    }

    function open(img) {
        build();
        _img = img;
        /* min scale so the image always covers the viewport */
        _minScale = Math.max(VIEW_W / img.width, VIEW_H / img.height);
        _scale = _minScale;
        _ox = (VIEW_W - img.width * _scale) / 2;
        _oy = (VIEW_H - img.height * _scale) / 2;
        _modal.querySelector('.rb-photo-zoom').value = '1';
        paint();
        _modal.style.display = 'flex';
    }

    function close() {
        if (_modal) _modal.style.display = 'none';
        _drag = null;
    }

    function apply() {
        var out = document.createElement('canvas');
        out.width = OUT_W; out.height = OUT_H;
        var octx = out.getContext('2d');
        octx.fillStyle = '#fff';
        octx.fillRect(0, 0, OUT_W, OUT_H);
        /* map viewport → output: same crop window scaled up */
        var k = OUT_W / VIEW_W;
        octx.drawImage(_img, _ox * k, _oy * k, _img.width * _scale * k, _img.height * _scale * k);
        var dataUrl = out.toDataURL('image/jpeg', 0.86);
        close();
        if (_cb) _cb(dataUrl);
    }

    /* public: open the cropper for a File. callback(dataUrl) on apply. */
    function openFromFile(file, callback) {
        if (!file) return;
        if (file.size > MAX_BYTES) { toast(T('photo_too_large')); return; }
        if (!/^image\//.test(file.type)) { toast(T('photo_invalid')); return; }
        _cb = callback;
        var url = URL.createObjectURL(file);
        var img = new Image();
        img.onload = function () { URL.revokeObjectURL(url); open(img); };
        img.onerror = function () { URL.revokeObjectURL(url); toast(T('photo_invalid')); };
        img.src = url;
    }

    globalThis.RbPhoto = { openFromFile: openFromFile, close: close };
})();
